import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title, Meta } from '@angular/platform-browser';
import { ReCaptcha2Component } from 'ngx-captcha';
import { finalize } from 'rxjs/operators';
import { EnvironmentService } from 'src/app/services/environment.service';
import { FacilityService } from 'src/app/services/facility.service';
import { FacilityConfig, FundraisersContent, FundraisersPageConfig } from 'src/environments/environment.interface';
import { mergeFundraisersContent, setCanonicalHref } from '../steelhaus-page.util';

/**
 * Steelhaus "Fundraisers" landing page.
 * Inquiry posts to Facility/FundraiserRequest (separate from Contact Us).
 * Captcha is a client-side UI gate only — same as Contact Us / SupportRequest
 * (token is not sent or verified server-side; API uses IP rate limiting).
 */
@Component({
  selector: 'app-fundraisers',
  templateUrl: './fundraisers.component.html',
  styleUrls: ['./fundraisers.component.scss'],
})
export class FundraisersComponent implements OnInit {
  @ViewChild(ReCaptcha2Component) captchaComponent?: ReCaptcha2Component;
  settings: FacilityConfig;
  pageConfig?: FundraisersPageConfig;
  showPage = false;
  submitting = false;
  success = false;
  error = '';
  form: FormGroup;
  siteKey = '';
  captchaIsLoaded = false;
  captchaSuccess = false;
  captchaIsExpired = false;
  captchaResponse?: string;
  content: Required<FundraisersContent>;

  constructor(
    private env: EnvironmentService,
    private title: Title,
    private meta: Meta,
    private fb: FormBuilder,
    private facilityService: FacilityService
  ) {
    this.settings = this.env.getFacilityConfig();
    this.pageConfig = this.settings.fundraisersPage;
    this.content = mergeFundraisersContent(this.pageConfig?.content);
    this.siteKey = this.settings.recaptcha?.siteKey ?? '';
    this.form = this.fb.group({
      organizer: ['', Validators.required],
      organization: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,10}$'),
        ],
      ],
      phone: ['', Validators.required],
      date1: ['', Validators.required],
      date2: [''],
      nonprofit: ['', Validators.required],
      details: [''],
      captchaResponseControl: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.showPage = !!this.pageConfig?.show;
    if (!this.showPage) {
      return;
    }
    const pageTitle = `${this.pageConfig?.metaTitle ?? 'Fundraisers'} — ${this.settings.name}`;
    const desc =
      this.pageConfig?.metaDescription ??
      'Host your next fundraiser at Steelhaus. Book the studio, bring your community, and ride for your cause.';
    this.title.setTitle(pageTitle);
    this.setSocialMeta(pageTitle, desc);
  }

  private setSocialMeta(pageTitle: string, description: string): void {
    const meta = (this.settings.meta as any) || {};
    const url = typeof window !== 'undefined' ? window.location.href : '';
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: meta.ogImage || '' });
    this.meta.updateTag({ property: 'og:url', content: url });
    setCanonicalHref(url);
  }

  handleSuccess(captchaResponse: string | Event): void {
    const token =
      typeof captchaResponse === 'string'
        ? captchaResponse
        : (captchaResponse.target as HTMLInputElement | undefined)?.value ?? '';
    this.captchaResponse = token;
    this.form.controls['captchaResponseControl'].setValue(token);
    this.captchaSuccess = true;
    this.captchaIsExpired = false;
  }

  handleLoad(): void {
    this.captchaIsLoaded = true;
  }

  handleExpire(): void {
    this.captchaIsExpired = true;
    this.captchaSuccess = false;
    this.form.controls['captchaResponseControl'].reset();
  }

  resetCaptcha(): void {
    this.captchaSuccess = false;
    this.captchaIsExpired = false;
    this.captchaResponse = undefined;
    this.form.controls['captchaResponseControl'].reset();
    this.captchaComponent?.resetCaptcha();
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  private canSubmit(): boolean {
    const { organizer, organization, email, phone, date1, nonprofit, captchaResponseControl } =
      this.form.getRawValue();
    if (
      !organizer?.trim() ||
      !organization?.trim() ||
      !email?.trim() ||
      !phone?.trim() ||
      !date1 ||
      !nonprofit
    ) {
      return false;
    }
    if (this.form.controls['email'].invalid) {
      return false;
    }
    return !!captchaResponseControl?.trim() && this.form.controls['captchaResponseControl'].valid;
  }

  onSubmit(): void {
    if (this.submitting) {
      return;
    }

    this.success = false;
    this.error = '';

    if (!this.canSubmit()) {
      this.form.markAllAsTouched();
      this.error = 'Please complete all required fields and captcha.';
      return;
    }

    this.submitting = true;

    const v = this.form.getRawValue();
    this.facilityService
      .submitFundraiserRequest({
        organizer: v.organizer,
        organization: v.organization,
        email: v.email,
        phone: v.phone,
        preferredDate1: v.date1,
        preferredDate2: v.date2 || '',
        nonprofit: v.nonprofit,
        details: v.details || '',
      })
      .pipe(finalize(() => {
        this.submitting = false;
      }))
      .subscribe({
        next: () => {
          this.success = true;
          this.form.reset({
            organizer: '',
            organization: '',
            email: '',
            phone: '',
            date1: '',
            date2: '',
            nonprofit: '',
            details: '',
            captchaResponseControl: '',
          });
          this.resetCaptcha();
        },
        error: (err) => {
          console.error('Error submitting fundraiser form:', err);
          this.error =
            err?.error?.message ||
            'Sorry — something went wrong sending your request. Please try again.';
          this.resetCaptcha();
        },
      });
  }
}
